<script setup lang="ts">
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useRequestSessionStore } from '@Stores/requestSession.store';
import { FlowLayout } from '@Shared';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import useFlowSteps from '@Use/useFlowSteps';
import useTaskNavTitle from '@Use/useTaskNavTitle';
import { useRouter } from 'vue-router';
import { ref, computed, useTemplateRef, nextTick, onMounted } from 'vue';
import { IdvDocumentMenuItems } from '@Constants/idvDocumentMenuItems.constants';
import type { IdvDocumentDisplay } from '@Types/MatchIdv.interface';
import { beginIdentityVerification, getCaptureOptionsApi } from '@Api/idv.api';
import { getCompanySlug } from 'host/mfeBridge';
import InstructionsCarousel from '@Components/match/InstructionsCarousel.vue';
import QrOptionForMatchIdv from '@Components/match/QrOptionForMatchIdv.vue';
import useIsMobile from '@Use/useIsMobile';
import type { StartCaptureFlowPayload } from '@Types/MatchIdv.interface';

const router = useRouter();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const requestSessionStore = useRequestSessionStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const { getProgressSteps, getCurrentStep } = useFlowSteps();
const steps = getProgressSteps();
const { taskNavTitle } = useTaskNavTitle();

const { isMobile } = useIsMobile();
const continueText = ref('Continue');
const docType = ref<IdvDocumentDisplay | undefined>(undefined);
const carousel = useTemplateRef('carousel');
const isLoading = ref(false);

// Defaults to asking. The vendor that needs a document type cannot start without one, so an
// unanswered options call must leave the picker in place rather than skip it.
const documentSelectionRequired = ref(true);

// Defaults to handing off. A real capture needs a camera, and a desktop cannot be assumed to
// have a usable one, so an unanswered options call must keep the QR route rather than start a
// capture in place that the recipient cannot finish.
const deviceHandoffRequired = ref(true);

// Nothing renders until the options call answers. The defaults above are deliberately the
// safe-but-wrong-for-Socure ones, so showing them first would flash a picker at a recipient
// whose vendor runs its own.
const captureOptionsResolved = ref(false);

onMounted(async () => {
    const companySlug = getCompanySlug();
    if (!companySlug) {
        captureOptionsResolved.value = true;
        return;
    }

    const options = await getCaptureOptionsApi(companySlug);
    documentSelectionRequired.value = options.documentSelectionRequired;
    deviceHandoffRequired.value = options.deviceHandoffRequired;
    captureOptionsResolved.value = true;

    // PLAT-3256: with the vendor running its own picker there is nothing left to ask on this
    // screen, and the priming sheet has already covered what the interstitial used to restate.
    // Hand off straight to the vendor instead of rendering a page whose only control advances.
    if (handsOffWithoutInput.value) await startCapture({ replaceHistory: true });
});


// COMPUTED
const selectedDisplayText = computed(() => {return docType.value?.display || 'Select';});

// A capture that photographs nothing needs no camera and therefore no second device, so the
// desktop starts it here instead of rendering a QR code that only a phone could act on.
const capturesOnThisDevice = computed(() => isMobile.value || !deviceHandoffRequired.value);

// This screen collects nothing: no document to pick, no second device to hand to.
const handsOffWithoutInput = computed(() => capturesOnThisDevice.value && !documentSelectionRequired.value);

const startCapture = async ({ replaceHistory = false } = {}) => {
    if (!requestSessionStore.requestId) {
        console.error('Missing requestId for identity verification');
        router.push({ name: 'general-error' });
        return;
    }

    const companySlug = getCompanySlug();
    if (!companySlug) {
        console.error('Missing company slug for identity verification');
        router.push({ name: 'general-error' });
        return;
    }

    isLoading.value = true;

    try {
        // Sending a type when the vendor runs its own picker would narrow the documents the
        // recipient is allowed to present, so it is left off entirely.
        const startCaptureFlowPayload: StartCaptureFlowPayload = documentSelectionRequired.value
            ? { documentType: docType.value?.value, companySlug }
            : { companySlug };
        const idvResponse = await beginIdentityVerification(startCaptureFlowPayload);
        if (!idvResponse?.captureUrl) {
            console.error('Failed to begin identity verification. No capture URL returned.');
            router.push({ name: 'general-error' });
            return;
        }
        // When this screen redirects on its own, it must not stay in history: back from the
        // vendor's consent screen would land here and immediately bounce the recipient forward
        // again. Replacing sends them back past it, to the dashboard they started from. The
        // gestured path keeps its entry, since returning to the picker is a real destination.
        if (replaceHistory) {
            window.location.replace(idvResponse.captureUrl);
        } else {
            window.location.href = idvResponse.captureUrl;
        }
    } catch (error) {
        console.error('Failed to begin identity verification. Error:', error);
        router.push({ name: 'general-error' });
    }
};

const nextStep = async () => {
    if (documentSelectionRequired.value && !docType.value) return;

    if (carousel.value && !carousel.value.isOnLastSlide()) {
        carousel.value?.next();
        return;
    }

    await startCapture();
};

const onSlideChanged = () => {
    continueText.value = carousel.value?.isOnLastSlide()
        ? 'Open Camera'
        : 'I Understand';
};

const setDocType = (doc: IdvDocumentDisplay) => {
    docType.value = doc;
    nextTick(() => {
        carousel.value?.reset();
    });
};
</script>


<template>
    <flow-layout
        is-white-label 
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :steps="steps"
        :current-step="getCurrentStep()">

        <template v-if="taskNavTitle" #header>{{ taskNavTitle }}</template>

        <app-form-content-layout>
            <div v-if="!captureOptionsResolved || handsOffWithoutInput" class="handoff-loader">
                <app-brand-loader data-testid="handoff-loader">
                    Getting your verification ready...
                </app-brand-loader>
            </div>

            <!-- Reaching here means the picker is required: the no-input case redirects above. -->
            <template v-else-if="capturesOnThisDevice">
                <div>
                    Choose the identification document that you're using for this identity verification.
                </div>
                <app-menu
                    v-if="selectedDisplayText"
                    branded
                    expanded
                    data-testid="doc-type-selector">
                    <template #trigger>
                        <app-input-select-trigger
                            size="large"
                            expanded>
                            {{ selectedDisplayText }}
                        </app-input-select-trigger>
                    </template>
                    <app-menu-item
                        v-for="item in IdvDocumentMenuItems"
                        :key="item.docType.value"
                        @onClick="setDocType(item.docType)">
                        {{ item.docType.display }}
                        <template #description>
                            <div>{{ item.description }}</div>
                        </template>
                        <template #subtext>
                            <div>{{ item.subtext }}</div>
                        </template>
                    </app-menu-item>
                </app-menu>
            
                <div v-if="!!docType"
                     class="continue-container"
                     :class="{ 'instructions-hidden': !docType, 'instructions-active' : !!docType }">
                    <InstructionsCarousel
                        ref="carousel"
                        class="doc-type-carousel"
                        :doc-type="docType"
                        @slide-changed="onSlideChanged" />
                    <app-button
                        expanded
                        :is-loading="isLoading"
                        size="large"
                        type="primary"
                        data-testid="continue-identity"
                        @onClick="nextStep">
                        {{ continueText }}
                    </app-button>
                </div>
            </template>

            <qr-option-for-match-idv v-else />
        </app-form-content-layout>
    </flow-layout>
</template>


<style lang="scss" scoped>
.verification-doc-type-view {
    display: flex;
    flex-direction: column;
    height: 100%;

    &__inner-text-container {
        margin-top: 0.5rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }
}

.handoff-loader {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
}

.continue-container {
    position: relative;
    width: 100%;
    margin: auto;
    display: flex;
    flex-direction: column;
    align-items: center;

    &.instructions-hidden {
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.8s ease, visibility 0.8s ease;
    }

    &.instructions-active {
        opacity: 1;
        visibility: visible;
        transition: opacity 0.8s ease, visibility 0.8s ease;
    }
}
</style>